/* eslint-disable max-lines-per-function */
/* eslint-disable indent */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import GTM from 'extensions/gtm';
import 'extensions/ui-components/x-card';
import 'extensions/ui-components/x-carousel';
import 'extensions/ui-components/x-product-card';
import {LazyLoadController, SalesBrandsController} from 'extensions/ui-controllers';
import {LitElement, html, nothing} from 'lit';
import {styleMap} from 'lit/directives/style-map.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {RecommendationsService, TrackingService} from '../../../services';
import stylesheet from './x-recommendations-app.component.scss?as=stylesheet';

export class RecommendationsApp extends MobxReactionUpdate(LitElement) {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			margin: {
				type: String
			},
			product: {
				type: String
			},
			recommendationsService: {
				state: true,
				type: Object
			},
			store: {
				state: true,
				type: Object
			},
			trackingService: {
				state: true,
				type: Object
			},
			url: {
				type: String
			},
			widgetId: {
				attribute: 'widget-id',
				type: String
			}
		};
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		/**
		 * @type {RecommendationsService}
		 */
		this.recommendationsService = new RecommendationsService({
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID
		});

		/**
		 * @type {TrackingService}
		 */
		this.trackingService = new TrackingService({
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID
		});

		/**
		 * @type {import('../../../services').RecommendationsStore}
		 */
		this.store = {};

		/**
		 * @type {string[]}
		 */
		this.saleBrands = [];

		/**
		 * @type {string}
		 */
		this.margin = '48px 0';

		/**
		 * @type {string}
		 */
		this.product = '';

		/**
		 * @type {string}
		 */
		this.widgetId = '';

		// Create controllers
		this.lazyLoadController = new LazyLoadController(this, this.onLazy);
		this.salesBrandsController = new SalesBrandsController(this);
	}

	async connectedCallback () {
		super.connectedCallback();

		this.recommendationsService.onEvent('afterRecommendations', this.sendImpressions.bind(this));

		this.recommendationsService.getRecommendations(this.widgetId, this.product);
		// Wait for service store to be fully initialized before setting initial value
		await this.recommendationsService.store.initialized;

		this.store = this.recommendationsService.store;
	}

	/**
	 * Intersection observer callback
	 */
	async onLazy () {
		// Get sale brands
		this.saleBrands = await this.salesBrandsController.getSalesBrands();
	}

	handleProductClick (product) {
		// Send request to tracking endpoint
		this.trackingService.trackRecommendationClick(this.widgetId, this.widget.requestId, product.parent_product || product.id, product.url);
	}

	sendImpressions (data) {
		const widget = data?.widgets?.find(w => {
			return w.id === this.widgetId;
		});

		if (!widget?.items?.length) {
			return;
		}

		const gtmList = `Recommendation: ${widget?.title}`;
		this.gtm.hawkSearchSendImpressions(this.gtm.slugify(gtmList), gtmList, widget?.items);
	}

	get gtmList () {
		return `Recommendation: ${this.widget?.title}`;
	}

	get widget () {
		return this.store?.state?.widgets?.find(widget => {
			return widget.id === this.widgetId;
		});
	}

	renderProductResults () {
		return this.widget?.items?.map(product => {
			return html`
				<x-card type="product">
					<x-product-card
						@click="${e => this.handleProductClick(product, e)}"
						brand="${product?.attributes?.brand}"
						code="${product.id}"
						?discounted-brand="${product?.attributes?.discounted_brands === 'yes'}"
						flag="${product?.attributes?.product_flag}"
						formatted-price="${product.formattedPrice}"
						formatted-retail="${product.formattedRetail}"
						gtm-list="${this.gtmList}"
						?has-metal-options="${product?.attributes?.has_metal_options}"
						hover-image-url="${product?.attributes?.product_image_two_resized || product?.attributes?.product_image_two}"
						image-url="${product?.imageUrl}"
						?incart-pricing="${product?.attributes?.incart_pricing}"
						name="${product.title}"
						price="${product.price}"
						retail="${product.retail}"
						sale-brands="${JSON.stringify(this.saleBrands)}"
						starting-at="${product?.attributes?.starting_at}"
						variant-swatch="${typeof product?.attributes?.options_variant_swatch === 'string' ? product?.attributes?.options_variant_swatch : JSON.stringify(product?.attributes?.options_variant_swatch)}"
						url="${product.url}"
					></x-product-card>
				</x-card>
			`;
		});
	}

	render () {
		if (!this.widget?.items?.length) {
			return nothing;
		}

		const styles = {
			margin: this.margin
		};

		return html`
			<div class="container" style="${styleMap(styles)}">
				<h2 class="title">${unsafeHTML(this.widget?.title)}</h2>
				<x-carousel
					?dots="${this.widget?.carouselData?.showDots}"
					?disable-arrows="${!this.widget?.carouselData?.showNextPrevButtons}"
					?disable-infinite="${!this.widget?.carouselData?.isCircular}"
					slides-per-scroll="${this.widget?.carouselData?.scrollNumber}"
					slides-per-view="${this.widget?.carouselData?.nofVisible}"
				>
					${this.renderProductResults()}
				</x-carousel>
			</div>
		`;
	}

};

window.customElements.define('x-recommendations-app', RecommendationsApp);
